import { DrawingUtils, type FaceLandmarkerResult, type NormalizedLandmark } from "@mediapipe/tasks-vision"

import { FACE_MESH_FINAL_LAYERS, FACE_MESH_LAYERS, type MeshConnectorStyle } from "./face-landmarks-config"

export interface DrawFaceMeshOptions {
  /**
   * 0..1 — fraction of each layer's connectors to draw, for the animated
   * "draw-on" effect. Omit (or 1) to draw the full mesh.
   */
  progress?: number
  /** Mirror horizontally — the front camera feed is mirrored. Default true. */
  mirror?: boolean
  /** Layer set to use. Defaults to the full styled mesh. */
  layers?: MeshConnectorStyle[]
}

function drawLandmarkSet(
  utils: DrawingUtils,
  landmarks: NormalizedLandmark[],
  layers: MeshConnectorStyle[],
  progress: number,
) {
  for (const layer of layers) {
    const partial =
      progress >= 1
        ? layer.connectors
        : layer.connectors.slice(0, Math.ceil(layer.connectors.length * progress))
    if (partial.length > 0) {
      utils.drawConnectors(landmarks, partial, { color: layer.color, lineWidth: layer.lineWidth })
    }
  }
}

/**
 * Draw the face mesh for every detected face onto a 2D canvas context. Clears
 * the canvas first and applies the mirror transform. With `progress < 1`, only
 * the leading fraction of each connector set is drawn — call it on a rising
 * `progress` (per animation cycle) to get the origene "scanning" draw-on.
 */
export function drawFaceMesh(
  ctx: CanvasRenderingContext2D,
  result: FaceLandmarkerResult,
  canvasWidth: number,
  canvasHeight: number,
  options: DrawFaceMeshOptions = {},
) {
  const { progress = 1, mirror = true, layers = FACE_MESH_LAYERS } = options
  if (!result?.faceLandmarks?.length) return

  const safe = Math.min(Math.max(progress, 0), 1)

  ctx.clearRect(0, 0, canvasWidth, canvasHeight)
  ctx.save()
  if (mirror) {
    ctx.scale(-1, 1)
    ctx.translate(-canvasWidth, 0)
  }

  const utils = new DrawingUtils(ctx)
  for (const landmarks of result.faceLandmarks) {
    drawLandmarkSet(utils, landmarks, layers, safe)
  }

  ctx.restore()
}

/** Draw the final settled mesh (full tesselation + face oval), no partial reveal. */
export function drawFinalFaceMesh(
  ctx: CanvasRenderingContext2D,
  result: FaceLandmarkerResult,
  canvasWidth: number,
  canvasHeight: number,
  mirror = true,
) {
  drawFaceMesh(ctx, result, canvasWidth, canvasHeight, {
    progress: 1,
    mirror,
    layers: FACE_MESH_FINAL_LAYERS,
  })
}
